import React, { useState } from "react";
import { Button, Popconfirm, Table } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import SupplierFormModal from "./SupplierFormModal";
import {
  useGetSuppliers,
  useAddSupplier,
  useEditSupplier,
  useDeleteSupplier,
} from "../../../hooks/useSuppliers";

const SuppliersScreen = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);

  const { data: suppliers = [], isLoading } = useGetSuppliers();
  const addSupplierMutation = useAddSupplier();
  const editSupplierMutation = useEditSupplier();
  const deleteSupplierMutation = useDeleteSupplier();

  const handleAdd = () => {
    setEditingSupplier(null);
    setIsModalVisible(true);
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setIsModalVisible(true);
  };

  const handleDelete = (id) => {
    deleteSupplierMutation.mutate(id);
  };

  const handleSubmit = async (values) => {
    if (editingSupplier) {
      editSupplierMutation.mutate(
        { supplierId: editingSupplier.id, values },
        {
          onSuccess: () => setIsModalVisible(false),
        },
      );
    } else {
      addSupplierMutation.mutate(values, {
        onSuccess: () => setIsModalVisible(false),
      });
    }
  };

  const columns = [
    {
      title: "Supplier ",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      width: 120,
    },
    {
      title: " Phone",
      dataIndex: "contactPhone",
      key: "contactPhone",
      width: 120,
    },
    {
      title: " Email",
      dataIndex: "contactEmail",
      key: "contactEmail",
      width: 130,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Contact Person",
      dataIndex: "contactPerson",
      key: "contactPerson",
      width: 140,
    },
    {
      title: "Person Phone",
      dataIndex: "contactPersonPhone",
      key: "contactPersonPhone",
      width: 130,
    },
    {
      title: "Person Email",
      dataIndex: "contactPersonEmail",
      key: "contactPersonEmail",
      width: 180,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <div className="flex gap-2">
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            type="text"
            className="text-blue-600 hover:text-blue-800"
          />
          <Popconfirm
            title="Are you sure to delete this supplier?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              type="text"
              loading={deleteSupplierMutation.isLoading}
            />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className="">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Suppliers Management</h2>
        <Button
          type=""
          size="middle"
          className="bg-gradient-to-r text-white from-primary to-primary_hover hover:bg-primary_hover hover:scale-105 hover:to-[#166575] border-0 shadow-lg hover:shadow-xl rounded-lg font-semibold transition-all"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Add Supplier
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={suppliers}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 200 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} suppliers`,
        }}
        className="shadow-md rounded-lg"
      />

      <SupplierFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        editingSupplier={editingSupplier}
        isLoading={
          addSupplierMutation.isLoading || editSupplierMutation.isLoading
        }
      />
    </div>
  );
};

export default SuppliersScreen;
